// src/components/so/SoPlanDispatchReport.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { decryptData } from "../../utils/encrypt";
import {
  SALES_ORDER_CUSTOMER_LIST,
  MATERIAL_LIST_ENDPOINT,
  GET_LOADING_POINTS_ENDPOINT,
  PACKING_TYPE_LIST_ENDPOINT,
  SO_PLAN_DISPATCH_REPORT_API,
  SO_PLAN_DISPATCH_DETAILS_API,
} from "../../config/api";
import Select from "react-select";

function SoPlanDispatchReport() {
  const [lookups, setLookups] = useState({
    customers: [],
    materials: [],
    packingTypes: ["25 Kgs Bags", "50 Kgs Bag", "Loose Bulker"],
    loadingPoints: [],
  });
  const [packingOptions, setPackingOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  // expand/collapse & row details cache
  const [expanded, setExpanded] = useState(new Set());
  const [details, setDetails] = useState({});
  const rowKey = (r) => `${r.sales_order_id}:${r.material_id}`;

  // authoritative totals from detail payload (computed automatically on load/search)
  const [overrides, setOverrides] = useState({}); // { key: { planned_qty:number, dispatch_qty:number } }
  const [verifyState, setVerifyState] = useState({}); // { key: 'pending'|'ok'|'mismatch'|'error' }

  // -------- Sorting --------
  const [sort, setSort] = useState({ by: "customer_name", dir: "asc" });
  const numericCols = new Set(["so_qty", "planned_qty", "dispatch_qty"]);
  const handleSort = (col) => {
    setSort((s) =>
      s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "asc" }
    );
  };
  const sortIndicator = (col) => (sort.by === col ? (sort.dir === "asc" ? "▲" : "▼") : "");
  const sortedRows = useMemo(() => {
    const data = rows.slice();
    const { by, dir } = sort;
    data.sort((a, b) => {
      const va = a?.[by];
      const vb = b?.[by];
      if (numericCols.has(by)) {
        const na = Number(va ?? 0);
        const nb = Number(vb ?? 0);
        return dir === "asc" ? na - nb : nb - na;
      }
      const sa = (va ?? "").toString();
      const sb = (vb ?? "").toString();
      const cmp = sa.localeCompare(sb);
      return dir === "asc" ? cmp : -cmp;
    });
    return data;
  }, [rows, sort]);

  // dates
  const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const computeDefaultDates = () => {
    const now = new Date();
    const from = new Date(now); from.setDate(from.getDate() - 14);
    const to = new Date(now);   to.setDate(to.getDate() + 14);
    return { from: toYMD(from), to: toYMD(to) };
  };

  const [filters, setFilters] = useState(() => {
    const { from, to } = computeDefaultDates();
    return {
      customer_id: "",
      material_id: "",
      packing_type: "",
      loading_point_id: "",
      delivery_from: from,
      delivery_to: to,
      invoiced_status: "",
      vehicle_status: "",
      logistic_status: "",
    };
  });

  // react-select options (Customer)
  const customerOptions = useMemo(
    () => (lookups.customers || []).map((c) => ({ value: String(c.id), label: c.account_name })),
    [lookups.customers]
  );
  const customerValue = useMemo(
    () => customerOptions.find((o) => o.value === String(filters.customer_id)) || null,
    [customerOptions, filters.customer_id]
  );

  // load packing types (dynamic)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(PACKING_TYPE_LIST_ENDPOINT);
        const data = await res.json();
        setPackingOptions(Array.isArray(data) ? data : []);
      } catch {
        setPackingOptions([]);
      }
    })();
  }, []);

  // user/org
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("suyog_user");
      if (storedUser) {
        const u = decryptData(storedUser);
        setUserId(u?.user_id || null);
      }
    } catch {}
    const storedOrg = localStorage.getItem("suyog_selected_org");
    if (storedOrg) {
      try {
        const parsedOrg = JSON.parse(storedOrg);
        setOrgId(parsedOrg?.value || null);
      } catch {}
    }
  }, []);

  // dropdowns + first fetch
  useEffect(() => {
    if (!userId || !orgId) return;
    const loadDropdowns = async () => {
      try {
        const cRes = await fetch(SALES_ORDER_CUSTOMER_LIST, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const cJson = await cRes.json();
        const customers = Array.isArray(cJson) ? cJson : (cJson?.data || []);

        const mRes = await fetch(MATERIAL_LIST_ENDPOINT);
        const mJson = await mRes.json();
        const materials = Array.isArray(mJson) ? mJson : (mJson?.data || []);

        const lpRes = await fetch(GET_LOADING_POINTS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ org_id: orgId }),
        });
        const lpJson = await lpRes.json();
        const loadingPoints =
          lpJson?.success && Array.isArray(lpJson.records) ? lpJson.records : [];

        setLookups((prev) => ({ ...prev, customers, materials, loadingPoints }));
      } catch (e) {
        console.error("Dropdowns load error:", e);
      }
    };
    loadDropdowns();
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orgId]);

  // API: main report
  const fetchReport = async (overrideFilters) => {
    if (!userId || !orgId) return;
    setLoading(true);
    try {
      const payloadFilters = overrideFilters ?? filters;
      const res = await fetch(SO_PLAN_DISPATCH_REPORT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, org_id: orgId, ...payloadFilters }),
      });
      const data = await res.json();
      const nextRows = data?.success ? (data.rows || []) : [];
      setRows(nextRows);

      // clear previous verification/overrides
      setOverrides({});
      setVerifyState({});
      setExpanded(new Set());
      setDetails({});

      // verify all rows immediately to correct figures in header/footer
      void verifyAllRows(nextRows, userId, orgId);
    } catch (e) {
      console.error("Report fetch error:", e);
      setRows([]);
      setOverrides({});
      setVerifyState({});
    } finally {
      setLoading(false);
    }
  };

  // collapse all expanded rows + clear cached details and verification states
  const collapseAll = useCallback(() => {
    setExpanded(new Set());
    setDetails({});
    setOverrides({});
    setVerifyState({});
  }, []);

  // filters
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };
  const handleSearch = (e) => {
    e.preventDefault();
    collapseAll();
    fetchReport();
  };
  const handleReset = () => {
    const { from, to } = computeDefaultDates();
    const nextFilters = {
      ...filters,
      customer_id: "",
      material_id: "",
      packing_type: "",
      loading_point_id: "",
      delivery_from: from,
      delivery_to: to,
      invoiced_status: "",
      vehicle_status: "",
      logistic_status: "",
    };
    setFilters(nextFilters);
    collapseAll();
    fetchReport(nextFilters);
  };

  // helpers to sum
  const sumPlans = (plans = []) => plans.reduce((acc, p) => acc + Number(p?.planned_qty || 0), 0);
  const sumDispatches = (dispatches = []) => dispatches.reduce((acc, d) => acc + Number(d?.qty || 0), 0);

  // verify all rows right after list load/search (safe concurrency)
  const verifyAllRows = async (rowsList, uid, oid) => {
    const tasks = rowsList.map((r) => async () => {
      const k = rowKey(r);
      setVerifyState((s) => ({ ...s, [k]: "pending" }));
      try {
        const res = await fetch(SO_PLAN_DISPATCH_DETAILS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: uid,
            org_id: oid,
            sales_order_id: r.sales_order_id,
            material_id: r.material_id,
          }),
        });
        const json = await res.json();
        if (json?.success) {
          const planSum = sumPlans(json.plans);
          const dispSum = sumDispatches(json.dispatches);
          setOverrides((o) => ({ ...o, [k]: { planned_qty: planSum, dispatch_qty: dispSum } }));
          const mismatch =
            Math.abs(Number(planSum) - Number(r.planned_qty || 0)) > 0.0005 ||
            Math.abs(Number(dispSum) - Number(r.dispatch_qty || 0)) > 0.0005;
          setVerifyState((s) => ({ ...s, [k]: mismatch ? "mismatch" : "ok" }));
          // cache details so drilling down opens instantly
          setDetails((d) => ({ ...d, [k]: { loading: false, error: null, data: json } }));
        } else {
          setVerifyState((s) => ({ ...s, [k]: "error" }));
        }
      } catch {
        setVerifyState((s) => ({ ...s, [k]: "error" }));
      }
    });

    const CONCURRENCY = 6;
    let i = 0;
    const runners = Array.from({ length: Math.min(CONCURRENCY, tasks.length) }, async () => {
      while (i < tasks.length) {
        const idx = i++;
        await tasks[idx]();
      }
    });
    await Promise.all(runners);
  };

  // expand/collapse (uses cached details from verification if available)
  const toggleExpand = async (r) => {
    const k = rowKey(r);
    const isOpen = expanded.has(k);
    if (isOpen) {
      const next = new Set(expanded); next.delete(k);
      setExpanded(next);
      return;
    }
    if (!details[k]?.data && !details[k]?.loading) {
      setDetails((d) => ({ ...d, [k]: { loading: true, error: null, data: null } }));
      try {
        const res = await fetch(SO_PLAN_DISPATCH_DETAILS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            org_id: orgId,
            sales_order_id: r.sales_order_id,
            material_id: r.material_id,
          }),
        });
        const json = await res.json();
        if (json?.success) {
          setDetails((d) => ({ ...d, [k]: { loading: false, error: null, data: json } }));
          if (!overrides[k]) {
            const planSum = sumPlans(json.plans);
            const dispSum = sumDispatches(json.dispatches);
            setOverrides((o) => ({ ...o, [k]: { planned_qty: planSum, dispatch_qty: dispSum } }));
          }
        } else {
          setDetails((d) => ({ ...d, [k]: { loading: false, error: json?.error || "Failed to load", data: null } }));
        }
      } catch (e) {
        setDetails((d) => ({ ...d, [k]: { loading: false, error: e.message, data: null } }));
      }
    }
    const next = new Set(expanded); next.add(k);
    setExpanded(next);
  };

  // Totals in footer: use overrides when available
  const totals = useMemo(() => {
    let so = 0, plan = 0, disp = 0;
    for (const r of rows) {
      const k = rowKey(r);
      const ov = overrides[k];
      so  += Number(r.so_qty || 0);
      plan += Number((ov?.planned_qty ?? r.planned_qty) || 0);
      disp += Number((ov?.dispatch_qty ?? r.dispatch_qty) || 0);
    }
    return { so, plan, disp };
  }, [rows, overrides]);

  const fmt3 = (n) => Number(n ?? 0).toFixed(3);

  const cellWithVerify = (r, type /* 'planned'|'dispatch' */) => {
    const k = rowKey(r);
    const ov = overrides[k];
    const original = type === "planned" ? r.planned_qty : r.dispatch_qty;
    const display = type === "planned" ? ov?.planned_qty ?? original : ov?.dispatch_qty ?? original;

    const state = verifyState[k]; // pending | ok | mismatch | error
    const showStar =
      state === "mismatch" &&
      Math.abs(Number(display ?? 0) - Number(original ?? 0)) > 0.0005;

    return (
      <span
        title={
          state === "pending"
            ? "Verifying…"
            : state === "mismatch"
              ? `Recomputed from details (was ${fmt3(original)})`
              : state === "error"
                ? "Verification failed"
                : "Verified"
        }
      >
        {fmt3(display)}
        {state === "pending" ? (
          <span className="ms-1 spinner-border spinner-border-sm" style={{ width: "0.7rem", height: "0.7rem" }} />
        ) : showStar ? "" : null}
      </span>
    );
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4>📊 SO vs Planning vs Dispatch</h4>
        <div className="small text-muted mb-1">
          <span className="me-3">
            <span className="align-middle me-1" style={{ display: "inline-block", width: 10, height: 10, borderRadius: 10, background: "#6c757d" }} /> 
            verifying on load…
          </span>
          * value adjusted from detail totals.
        </div>

        {/* Filters */}
        <form className="row g-3 mt-2" onSubmit={handleSearch}>
          <div className="col-md-3">
            <label className="form-label">Customer</label>
            <Select
              inputId="customer-select"
              isClearable
              placeholder="-- All Customers --"
              options={customerOptions}
              value={customerValue}
              onChange={(opt) => setFilters((f) => ({ ...f, customer_id: opt ? opt.value : "" }))}
              classNamePrefix="react-select"
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Material</label>
            <select className="form-select" name="material_id" value={filters.material_id} onChange={handleChange}>
              <option value="">-- All Materials --</option>
              {lookups.materials.map((m) => (
                <option key={m.id} value={m.id}>{m.material_name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Packing Type</label>
            <select
              className="form-select"
              name="packing_type"
              value={filters.packing_type}
              onChange={(e) => setFilters(f => ({ ...f, packing_type: e.target.value }))}
            >
              <option value="" disabled={!packingOptions.length}>
                {packingOptions.length ? "-- All Packing --" : "Loading..."}
              </option>
              {packingOptions.map((p) => {
                const label = p.name ?? p.packing_type;
                return <option key={p.id ?? label} value={label}>{label}</option>;
              })}
              {packingOptions.length === 0 &&
                lookups.packingTypes.map((txt) => <option key={txt} value={txt}>{txt}</option>)
              }
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Loading Point</label>
            <select className="form-select" name="loading_point_id" value={filters.loading_point_id} onChange={handleChange}>
              <option value="">-- All Loading Points --</option>
              {lookups.loadingPoints.map((lp) => (
                <option key={lp.id} value={lp.id}>{lp.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Delivery From</label>
            <input type="date" className="form-control" name="delivery_from" value={filters.delivery_from} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Delivery To</label>
            <input type="date" className="form-control" name="delivery_to" value={filters.delivery_to} onChange={handleChange} />
          </div>

          <div className="col-md-2 d-none">
            <label className="form-label">Vehicle Status</label>
            <select className="form-select" name="vehicle_status" value={filters.vehicle_status} onChange={handleChange}>
              <option value="">All</option><option>Pending</option><option>Done</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Logistic Planned</label>
            <select className="form-select" name="logistic_status" value={filters.logistic_status} onChange={handleChange}>
              <option value="">All</option><option>Pending</option><option>Done</option>
            </select>
          </div>

          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? "Loading..." : "Search"}
            </button>
            <button className="btn btn-secondary" type="button" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>

        {/* Results */}
        <div className="table-responsive mt-4">
          <table className="table table-bordered table-hover w-100">
            <thead className="table-light">
              <tr>
                <th style={{ width: "2.5rem" }}></th>
                <th onClick={() => handleSort("customer_name")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Customer {sortIndicator("customer_name")}
                </th>
                <th onClick={() => handleSort("customer_po_no")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Customer PO No {sortIndicator("customer_po_no")}
                </th>
                <th onClick={() => handleSort("material_name")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Material {sortIndicator("material_name")}
                </th>
                <th className="text-end" onClick={() => handleSort("so_qty")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  PO Qty {sortIndicator("so_qty")}
                </th>
                <th className="text-end" onClick={() => handleSort("planned_qty")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Planned Qty {sortIndicator("planned_qty")}
                </th>
                <th className="text-end" onClick={() => handleSort("dispatch_qty")} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  Dispatch Qty {sortIndicator("dispatch_qty")}
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedRows.length === 0 ? (
                <tr><td colSpan="7" className="text-center">No data</td></tr>
              ) : sortedRows.map((r, i) => {
                const k = rowKey(r);
                const isOpen = expanded.has(k);
                const det = details[k];

                return (
                  <React.Fragment key={`${r.sales_order_id}-${r.material_id}-${i}`}>
                    <tr>
                      <td className="text-center align-middle">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          aria-expanded={isOpen}
                          aria-controls={`row-${r.sales_order_id}-${r.material_id}`}
                          onClick={() => toggleExpand(r)}
                          title={isOpen ? "Collapse" : "Expand"}
                        >
                          {isOpen ? "▼" : "▶"}
                        </button>
                      </td>
                      <td>{r.customer_name}</td>
                      <td>{r.customer_po_no}</td>
                      <td>{r.material_name}</td>
                      <td className="text-end">{fmt3(r.so_qty)}</td>
                      <td className="text-end">{cellWithVerify(r, "planned")}</td>
                      <td className="text-end">{cellWithVerify(r, "dispatch")}</td>
                    </tr>

                    {isOpen && (
                      <tr id={`row-${r.sales_order_id}-${r.material_id}`}>
                        <td colSpan="7">
                          {!det || det.loading ? (
                            <div className="d-flex align-items-center gap-2">
                              <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <span>Loading details…</span>
                            </div>
                          ) : det.error ? (
                            <div className="text-danger">Error: {det.error}</div>
                          ) : (
                            <div className="mt-2">
                              {/* Header summary */}
                              <div className="row g-3">
                                <div className="col-md">
                                  <div className="fw-bold">Order: {det.data.header.order_no}</div>
                                  <div>Customer: {det.data.header.customer_name}</div>
                                  <div>Material: {det.data.header.material_name}</div>
                                </div>
                                <div className="col-md">
                                  <div>PO No: {det.data.header.customer_po_no || "-"}</div>
                                  <div>PO Date: {det.data.header.po_date || "-"}</div>
                                </div>
                                <div className="col-md text-end">
                                  <div>Total SO: <strong>{fmt3(det.data.totals.so_qty)}</strong></div>
                                  <div>Total Planned: <strong>{fmt3(det.data.plans.reduce((a,p)=>a+Number(p.planned_qty||0),0))}</strong></div>
                                  <div>Total Dispatched: <strong>{fmt3(det.data.dispatches.reduce((a,d)=>a+Number(d.qty||0),0))}</strong></div>
                                </div>
                              </div>

                              {/* Subtables */}
                              <div className="row g-3 mt-2">
                                <div className="col-lg-6">
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Delivery Date</th>
                                          <th>Packing</th>
                                          <th>Destination</th>
                                          <th className="text-end">Planned Qty</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {det.data.plans.length === 0 ? (
                                          <tr><td colSpan="4" className="text-center">No plans</td></tr>
                                        ) : det.data.plans.map(p => (
                                          <tr key={p.id}>
                                            <td>{p.delivery_date || ""}</td>
                                            <td>{p.packing_style || ""}</td>
                                            <td>{p.destination || ""}</td>
                                            <td className="text-end">{fmt3(p.planned_qty || 0)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="table-light">
                                        <tr>
                                          <td colSpan="3" className="text-end fw-bold">Total:</td>
                                          <td className="text-end fw-bold">
                                            {fmt3(det.data.plans.reduce((a,p)=>a+Number(p.planned_qty||0),0))}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>

                                <div className="col-lg-6">
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Dispatch Date</th>
                                          <th>Vehicle</th>
                                          <th className="text-end">Qty</th>
                                          <th>Transporter</th>
                                          <th>Invoice No(s)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {det.data.dispatches.length === 0 ? (
                                          <tr><td colSpan="5" className="text-center">No dispatches</td></tr>
                                        ) : det.data.dispatches.map(d => {
                                          const invNos = det.data.invoices
                                            .filter(inv => inv.dispatch_id === d.id)
                                            .map(inv => inv.tally_invoice_no)
                                            .join(", ");
                                          return (
                                            <tr key={d.id}>
                                              <td>{d.dispatch_date || ""}</td>
                                              <td>{d.vehicle_no || ""}</td>
                                              <td className="text-end">{fmt3(d.qty || 0)}</td>
                                              <td>{d.transporter_name || d.sub_transporter_name || ""}</td>
                                              <td>{invNos || "-"}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                      <tfoot className="table-light">
                                        <tr>
                                          <td colSpan="2" className="text-end fw-bold">Total:</td>
                                          <td className="text-end fw-bold">
                                            {fmt3(det.data.dispatches.reduce((a,d)=>a+Number(d.qty||0),0))}
                                          </td>
                                          <td colSpan="2"></td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>

            <tfoot className="table-light">
              <tr>
                <td colSpan="4" className="text-end fw-bold">Totals:</td>
                <td className="text-end fw-bold">{fmt3(totals.so)}</td>
                <td className="text-end fw-bold">{fmt3(totals.plan)}</td>
                <td className="text-end fw-bold">{fmt3(totals.disp)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default SoPlanDispatchReport;
